import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { AppColors } from "../../../../util/AppColors";

// Redux imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomSelect from "../../../../components/Select/Select";
import { getSpeciesList } from "../../../../redux/reducers/speciesSlice";
import {
  createVital,
  deleteVital,
  getAllVitals,
  updateVital,
} from "../../../../redux/reducers/vitalsSlice";
import { inputTypeEnum } from "../../../../util/enum";
import { tblGrayBack } from "../../../../util/object";

function Vitals({ groupId, groupName, onBack }) {
  const dispatch = useDispatch();
  const fetchRef = useRef(false);

  // Local state
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const { vitals, loading } = useSelector((state) => state.vital);
  const groupList = useSelector((state) => state.group.list);
  const speciesList = useSelector((state) => state.species.list);
  const { selectedClinicBranch } = useSelector((state) => state?.clinic);
  const [selectedSpecies, setSelectedSpecies] = useState("");
  const role = localStorage.getItem("role");

  const [form, setForm] = useState({
    vitalName: "",
    unit: "",
    inputType: "",
    referenceRanges: [
      // { species: "", lower: "", upper: "" }
    ],
    groupName: [],
    dropdown: [],
    dropValue: "",
  });

  const [editForm, setEditForm] = useState({
    vitalName: "",
    unit: "",
    inputType: "",
    referenceRanges: [
      // { species: "", lower: "", upper: "" }
    ],
    groupName: "",
    dropdown: [],
    dropValue: "",
  });
  // const inputTypeOptions = ["Numeric", "Text Box", "Drop Down"];
  const inputEnum = {
    Numeric: "Numeric",
    "Text Box": "TextBox",
    "Drop Down": "DropDown",
  };
  const [groupOptions, setGroupOptions] = useState([]);
  // const inputTypeOptions = ["1", "2", "3", "4", "5", "6"];
  const inputTypeOptions = ["Numeric", "Text Box", "Drop Down"];

  // Fetch vitals when component mounts or groupId changes
  useEffect(() => {
    if (groupId) {
      dispatch(getAllVitals(groupId));
    }
  }, [dispatch, groupId]);

  useEffect(() => {
    if (fetchRef.current) return;
    if (role === "ROLE_DOCTOR") {
      if (!selectedClinicBranch?.branchId) return;
      fetchRef.current = true;
      const reqUrl = `?clinicId=${selectedClinicBranch?.branchId}`;
      dispatch(getSpeciesList(reqUrl));
      return;
    }
    fetchRef.current = true;
    dispatch(getSpeciesList());
  }, [selectedClinicBranch, role, dispatch]);

  // useEffect(() => {
  //   dispatch(getSpeciesList());
  // }, [dispatch]);

  // Load group options from localStorage (keeping your existing logic)
  useEffect(() => {
    const storedGroups = localStorage.getItem("speciesList");
    if (storedGroups) {
      const parsed = JSON.parse(storedGroups);
      const names = parsed.map((item) => item.GroupName);
      setGroupOptions(names);
    }
  }, [openDrawer, openEditDialog]);

  // Set default group name when component receives groupName prop
  useEffect(() => {
    if (groupName) {
      setForm((prev) => ({ ...prev, groupName: groupName }));
    }
  }, [groupName]);

  // Reset drawer form
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIsEditMode(false);
    setForm({
      vitalName: "",
      unit: "",
      inputType: "",
      referenceRanges: [
        // { species: "", lower: "", upper: "" }
      ],
      groupName: groupName || "", // Keep the current group name
    });
  };

  const getGroupList = () => {
    const list = groupList?.map((item) => ({
      name: item?.groupName,
      value: item?.groupName,
      roomId: item?.roomId,
    }));
    return list ?? [];
  };

  // Save new vital
  const handleSave = () => {
    if (!groupId) {
      alert("Please select a group first");
      return;
    }

    const ref = form.referenceRanges[0];

    const payload = {
      vitalName: form.vitalName,
      unit: form.unit,
      inputType: inputEnum[form.inputType],
      groupName: form.groupName?.[0] ?? "",
      rrSpecies: ref?.species || "",
      lowerRange: ref?.lowerRange ?? ref?.lower ?? "",
      upperRange: ref?.upper || "",
      dropdown: form?.dropdown ?? [],
    };
    if (isEditMode) {
      const reqObj = { ...form, groupName: form.groupName?.[0] };
      dispatch(updateVital(reqObj)).then(() => {
        dispatch(getAllVitals(groupId));
        handleCloseDrawer();
      });
    } else {
      if (form.groupName?.length > 0) {
        form?.groupName?.forEach((item) => {
          const reqGrp = getGroupList()?.find((grp) => grp?.value === item);
          payload.groupName = reqGrp?.name;
          dispatch(
            createVital({ roomId: reqGrp?.roomId, formData: payload }),
          ).then(() => {
            dispatch(getAllVitals(groupId));
          });
          handleCloseDrawer();
        });
      }
      // dispatch(createVital({ roomId: groupId, formData: payload })).then(() => {
      //   dispatch(getAllVitals(groupId));
      //   handleCloseDrawer();
      // });
    }
  };

  // Open edit dialog
  const handleEdit = (index) => {
    const selectedVital = vitals[index];
    setForm({
      ...selectedVital,
      dropdown: selectedVital?.dropdown ?? [],
      groupName: [selectedVital?.groupName],
      inputType: inputTypeEnum[selectedVital?.inputType],
    });
    setIsEditMode(true);
    setEditIndex(index);
    setOpenDrawer(true);
  };

  // Update existing vital
  const handleUpdate = () => {
    dispatch(updateVital(editForm)).then(() => {
      dispatch(getAllVitals(groupId));
    });
    setOpenEditDialog(false);
    setEditForm({
      vitalName: "",
      unit: "",
      inputType: "",
      referenceRanges: [
        // { species: "", lower: "", upper: "" }
      ],
      groupName: "",
    });
  };

  // Delete vital
  const handleDelete = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleDelConfirm = () => {
    const selectedVital = vitals[deleteIndex];
    if (selectedVital?.vitalId) {
      dispatch(deleteVital(selectedVital.vitalId)).then(() => {
        dispatch(getAllVitals(groupId));
        handleDelModClose();
      });
    }
  };

  // Reference range handlers - Form
  const handleAddRow = () => {
    setForm((prev) => ({
      ...prev,
      referenceRanges: [
        ...prev.referenceRanges,
        { species: "", lower: "", upper: "" },
      ],
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    const updated = [...form.referenceRanges];
    updated[index][field] = value;
    setForm({ ...form, referenceRanges: updated });
  };

  const handleRemoveReference = (index) => {
    if (form.referenceRanges.length > 1) {
      const updated = form.referenceRanges.filter((_, i) => i !== index);
      setForm({ ...form, referenceRanges: updated });
    }
  };

  // Reference range handlers - Edit
  const handleEditAddRow = () => {
    setEditForm((prev) => ({
      ...prev,
      referenceRanges: [
        ...prev.referenceRanges,
        { species: "", lower: "", upper: "" },
      ],
    }));
  };

  const handleEditReferenceChange = (index, field, value) => {
    const updated = [...editForm.referenceRanges];
    updated[index][field] = value;
    setEditForm({ ...editForm, referenceRanges: updated });
  };

  const handleEditRemoveReference = (index) => {
    if (editForm.referenceRanges.length > 1) {
      const updated = editForm.referenceRanges.filter((_, i) => i !== index);
      setEditForm({ ...editForm, referenceRanges: updated });
    }
  };

  return (
    <Box
    // p={3}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <div className="flex-row-ali-cen cursor mb15" onClick={onBack}>
          <ArrowBackIcon
            sx={{ color: AppColors.appPrimary, width: 20, height: 20 }}
          />
          <Typography className="fs16 txt-semi-bold blue-color ml5">
            Back
          </Typography>
        </div>

        <Typography variant="h6" color="primary">
          {isEditMode ? "Edit Parameter" : `Parameters`}
        </Typography>

        {/* Add Button */}
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            onClick={() => setOpenDrawer(true)}
            disabled={!groupId}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Vital List Table */}
      <Box sx={{ mt: 2, maxHeight: "60vh", overflowY: "auto" }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  "Parameter Name",
                  "Unit",
                  "Input Type",
                  "Reference Range (Species)",
                  "Group",
                  "Created By",
                  "Created Date",
                  "Actions",
                ].map((li, i) => (
                  <TableCell
                    style={tblGrayBack}
                    className="txt-semi-bold fs12"
                    key={i + li}
                  >
                    {li}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : vitals?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                vitals.map((row, index) => (
                  <TableRow key={index} style={{ border: "1px solid #ccc" }}>
                    <TableCell>{row.vitalName}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell>{row.inputType}</TableCell>
                    <TableCell>
                      {row.referenceRanges?.map((ref, i) => (
                        <div key={i}>
                          <strong>{ref.species}</strong>: [{ref.lower} to{" "}
                          {ref.upper}]
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>{row.groupName}</TableCell>
                    <TableCell>{row.userName || "-"}</TableCell>
                    <TableCell>
                      {new Date(row.createdDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(index)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={500}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {isEditMode ? "Edit Parameter" : "Add Parameter"}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Parameter Name"
            value={form.vitalName}
            onChange={(e) => setForm({ ...form, vitalName: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          />

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Input Type"
                value={form.inputType}
                onChange={(e) =>
                  setForm({ ...form, inputType: e.target.value })
                }
                margin="normal"
                InputLabelProps={{
                  sx: {
                    position: "absolute",
                    top: "50%",
                    left: "14px",
                    transform: "translateY(-50%)",
                    transition: "all 0.2s ease-out",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      top: "0px",
                      transform: "translateY(-50%) scale(0.75)",
                      transformOrigin: "top left",
                    },
                  },
                }}
              >
                {inputTypeOptions.map((opt, i) => (
                  <MenuItem key={i} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {form?.inputType === "Numeric" ? (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unit"
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  margin="normal"
                  InputLabelProps={{
                    sx: {
                      position: "absolute",
                      top: "50%",
                      left: "14px",
                      transform: "translateY(-50%)",
                      transition: "all 0.2s ease-out",
                      "&.Mui-focused, &.MuiFormLabel-filled": {
                        top: "0px",
                        transform: "translateY(-50%) scale(0.75)",
                        transformOrigin: "top left",
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
          </Grid>

          {form?.inputType === "Drop Down" ? (
            <div className="mt10">
              <CustomSelect
                list={form?.dropdown}
                value={form?.dropValue}
                addNewItem
                label="Drop Down"
                handleChange={(e) => {
                  setForm({
                    ...form,
                    dropValue: e?.selcetdValue ?? null,
                    dropdown: e?.newList ?? [],
                  });
                }}
              />
            </div>
          ) : null}

          {form?.referenceRanges?.map((ref, idx) => (
            <Box key={idx} mt={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2">Reference Range</Typography>
                {form.referenceRanges.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveReference(idx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <TextField
                select
                fullWidth
                label="Species"
                value={ref.species}
                onChange={(e) =>
                  handleReferenceChange(idx, "species", e.target.value)
                }
                margin="dense"
                InputLabelProps={{
                  sx: {
                    position: "absolute",
                    top: "50%",
                    left: "14px",
                    transform: "translateY(-50%)",
                    transition: "all 0.2s ease-out",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      top: "0px",
                      transform: "translateY(-50%) scale(0.75)",
                      transformOrigin: "top left",
                    },
                  },
                }}
              >
                {speciesList.map((species) => (
                  <MenuItem key={species.speciesId} value={species.speciesName}>
                    {species.speciesName}
                  </MenuItem>
                ))}
              </TextField>
              {form?.inputType === "Drop Down" ? (
                <div className="mt10">
                  <TextField
                    select
                    fullWidth
                    label="Normal Value"
                    value={ref?.lowerRange ?? form?.lowerRange}
                    onChange={(e) =>
                      handleReferenceChange(idx, "lowerRange", e?.target?.value)
                    }
                    margin="dense"
                    InputLabelProps={{
                      sx: {
                        position: "absolute",
                        top: "50%",
                        left: "14px",
                        transform: "translateY(-50%)",
                        transition: "all 0.2s ease-out",
                        "&.Mui-focused, &.MuiFormLabel-filled": {
                          top: "0px",
                          transform: "translateY(-50%) scale(0.75)",
                          transformOrigin: "top left",
                        },
                      },
                    }}
                  >
                    {form?.dropdown?.map((drp, i) => (
                      <MenuItem key={i} value={drp}>
                        {drp}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              ) : (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Lower Range"
                      value={ref.lower}
                      onChange={(e) =>
                        handleReferenceChange(idx, "lower", e.target.value)
                      }
                      margin="dense"
                      InputLabelProps={{
                        sx: {
                          position: "absolute",
                          top: "50%",
                          left: "14px",
                          transform: "translateY(-50%)",
                          transition: "all 0.2s ease-out",
                          "&.Mui-focused, &.MuiFormLabel-filled": {
                            top: "0px",
                            transform: "translateY(-50%) scale(0.75)",
                            transformOrigin: "top left",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Upper Range"
                      value={ref.upper}
                      onChange={(e) =>
                        handleReferenceChange(idx, "upper", e.target.value)
                      }
                      margin="dense"
                      InputLabelProps={{
                        sx: {
                          position: "absolute",
                          top: "50%",
                          left: "14px",
                          transform: "translateY(-50%)",
                          transition: "all 0.2s ease-out",
                          "&.Mui-focused, &.MuiFormLabel-filled": {
                            top: "0px",
                            transform: "translateY(-50%) scale(0.75)",
                            transformOrigin: "top left",
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          ))}
          {form?.inputType === "Numeric" ? (
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{ mt: 1 }}
            >
              Add Reference
            </Button>
          ) : null}
          <div className="mt15">
            <CustomSelect
              list={getGroupList()}
              value={form?.groupName}
              handleChange={(e) => {
                setForm({ ...form, groupName: e });
              }}
              reason
              label="Group Name"
            />
          </div>
          {/* <TextField
            select
            fullWidth
            label="Group Name"
            value={form.groupName}
            onChange={(e) => setForm({ ...form, groupName: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          >
            {groupList.map((group, i) => (
              <MenuItem key={i} value={group.groupName}>
                {group.groupName}
              </MenuItem>
            ))}
          </TextField> */}

          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={handleSave}
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Vitals;
